import React from 'react';
import './styles.scss';
import { Avatar } from '../Avatar';
import { useConfigStore } from '@/hooks';
import { timestampToRelativeTimeText } from '@/utils';
import { useCustomComponent } from '@/hooks';
import { parseMessageContent } from '@/utils/message-content';
import Components from './components';

export interface ChatMessageProps {
  messages: { id: string; message: string | React.ReactNode }[];
  sender: 'user' | 'agent' | 'system';
  timestamp: number;
  agentName?: string;
  agentAvatarUrl?: string;
  contentFormat?: 'html' | 'text';
}

const ChatMessageComponent: React.FC<ChatMessageProps> = ({
  messages,
  sender,
  timestamp,
  agentName: messageAgentName,
  agentAvatarUrl,
  contentFormat,
}) => {
  const {
    theme: {
      branding: { avatars },
    },
    features: {
      chat: { agentName: configAgentName },
    },
  } = useConfigStore();

  const name = (() => {
    switch (sender) {
      case 'system':
        return null;
      case 'agent':
        // Use message-specific agent name if available, otherwise fall back to config
        return messageAgentName || configAgentName;
      case 'user':
        return 'You';
    }
  })();

  // Determine avatar URL: use agentAvatarUrl if provided, otherwise fall back to config avatar
  // Ensure avatarUrl and avatarAlt are always strings (config values might be functions)
  const configUrl = typeof avatars[0].url === 'string' ? avatars[0].url : String(avatars[0].url);
  const configAlt = typeof avatars[0].alt === 'string' ? avatars[0].alt : String(avatars[0].alt);
  const configAgentNameStr = typeof configAgentName === 'string' ? configAgentName : String(configAgentName);
  const avatarUrl = agentAvatarUrl || configUrl;
  const avatarAlt = agentAvatarUrl ? messageAgentName || configAgentNameStr || configAlt : configAlt;

  const renderContent = (message: string | React.ReactNode) => {
    if (typeof message !== 'string') return message;
    return parseMessageContent(message, contentFormat);
  };

  if (sender === 'system') {
    return messages.map(({ id, message }) => (
      <div key={id}>
        {typeof message === 'string' ? (
          <Components.MessageText size="xs" color="primary" weight="light">
            {renderContent(message)}
          </Components.MessageText>
        ) : (
          message
        )}
      </div>
    ));
  }

  return (
    <Components.Container sender={sender}>
      {sender === 'agent' && (
        <Avatar
          url={avatarUrl}
          alt={avatarAlt}
          height={'42px'}
          width={'42px'}
          border={false}
        />
      )}
      <div className="peptalk-chat-message-content-container fade-in-up">
        {messages.map(({ id, message }) => (
          <Components.Content key={id}>
            {typeof message === 'string' ? (
              <Components.MessageText size="lg" color="primary" weight="light">
                {renderContent(message)}
              </Components.MessageText>
            ) : (
              message
            )}
          </Components.Content>
        ))}
        <Components.Meta size="xs" color="tertiary" weight="light">
          {`${name} ${timestampToRelativeTimeText(timestamp)}`}
        </Components.Meta>
      </div>
    </Components.Container>
  );
};

export const ChatMessage: React.FC<ChatMessageProps> = (
  props: ChatMessageProps
) => {
  const Component = useCustomComponent({
    componentName: 'ChatMessage',
    Component: ChatMessageComponent,
  });

  return <Component {...props} />;
};
